import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate, useParams, Navigate, Outlet } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import TenantSwitcher from './components/TenantSwitcher';
import Dashboard from './pages/Dashboard';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import Transactions from './pages/Transactions';
import UnpaidReport from './pages/UnpaidReport';
import UnshippedReport from './pages/UnshippedReport';
import GiftCardReport from './pages/GiftCardReport';
import SyncShopify from './pages/SyncShopify';
import Login from './pages/Login';
import Register from './pages/Register';
import AdminUsers from './pages/AdminUsers';
import AdminTenants from './pages/AdminTenants';
import Profile from './pages/Profile';

// Navigation Icons
const Icons = {
  dashboard: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="9"/>
      <rect x="14" y="3" width="7" height="5"/>
      <rect x="14" y="12" width="7" height="9"/>
      <rect x="3" y="16" width="7" height="5"/>
    </svg>
  ),
  sync: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2v6h-6"/>
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8"/>
      <path d="M3 22v-6h6"/>
      <path d="M21 12a9 9 0 0 1-15 6.7L3 16"/>
    </svg>
  ),
  orders: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
      <line x1="3" y1="6" x2="21" y2="6"/>
      <path d="M16 10a4 4 0 0 1-8 0"/>
    </svg>
  ),
  transactions: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23"/>
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
  ),
  outstanding: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  tenants: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  ),
  users: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  logout: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
      <polyline points="16 17 21 12 16 7"/>
      <line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
  profile: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  ),
};

// Helper to build tenant path
const useTenantPath = () => {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { currentTenantSlug } = useAuth();
  const effectiveSlug = tenantSlug || currentTenantSlug;
  
  return (path: string) => {
    if (!effectiveSlug) return path;
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `/t/${effectiveSlug}${normalizedPath}`;
  };
};

// NavLink component with active state - uses tenant-aware paths
const NavLink: React.FC<{ to: string; icon?: React.ReactNode; children: React.ReactNode; isAdmin?: boolean }> = ({ 
  to, 
  icon, 
  children, 
  isAdmin 
}) => {
  const location = useLocation();
  const tenantPath = useTenantPath();
  const fullPath = tenantPath(to);
  
  // Check if active - match the path within tenant context
  const isActive = location.pathname === fullPath || 
    (to !== '/' && location.pathname.startsWith(fullPath));
  
  return (
    <Link 
      to={fullPath} 
      className={`nav-link ${isActive ? 'active' : ''} ${isAdmin ? 'nav-link-admin' : ''}`}
      style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
    >
      {icon}
      <span>{children}</span>
    </Link>
  );
};

// NavDropdown component for hierarchical navigation - uses tenant-aware paths
const NavDropdown: React.FC<{ 
  icon?: React.ReactNode; 
  label: string; 
  items: { to: string; label: string }[];
}> = ({ icon, label, items }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const tenantPath = useTenantPath();
  
  // Check if any item is active
  const isAnyActive = items.some(item => {
    const fullPath = tenantPath(item.to);
    return location.pathname === fullPath || location.pathname.startsWith(fullPath + '/');
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="nav-dropdown" ref={dropdownRef}>
      <button
        className={`nav-link nav-dropdown-trigger ${isAnyActive ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
      >
        {icon}
        <span>{label}</span>
        <svg 
          width="10" 
          height="10" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2"
          style={{ 
            marginLeft: '2px',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}
        >
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {isOpen && (
        <div className="nav-dropdown-menu">
          {items.map(item => {
            const fullPath = tenantPath(item.to);
            return (
              <Link
                key={item.to}
                to={fullPath}
                className={`nav-dropdown-item ${location.pathname === fullPath ? 'active' : ''}`}
                onClick={() => setIsOpen(false)}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

const NavBar: React.FC = () => {
  const { isAuthenticated, isAdmin, user, logout, hasTenant, currentTenant } = useAuth();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  return (
    <nav className="navbar">
      <div className="navbar-container">
        {isAuthenticated ? (
          <div className="navbar-links">
            <TenantSwitcher />
            {(hasTenant || tenantSlug) && (
              <>
                <NavLink to="/" icon={Icons.dashboard}>Dashboard</NavLink>
                <NavLink to="/sync" icon={Icons.sync}>Sync</NavLink>
                <NavLink to="/orders" icon={Icons.orders}>Orders</NavLink>
                <NavLink to="/transactions" icon={Icons.transactions}>Transactions</NavLink>
                <NavDropdown 
                  icon={Icons.outstanding} 
                  label="Reports"
                  items={[
                    { to: '/reports/unpaid', label: 'Unpaid (by date)' },
                    { to: '/reports/unshipped', label: 'Unshipped (by date)' },
                    ...(currentTenant?.gift_cards_enabled ? [{ to: '/reports/gift-cards', label: 'Gift Cards (by date)' }] : []),
                  ]}
                />
              </>
            )}
            {isAdmin && (
              <>
                <div style={{ 
                  width: '1px', 
                  height: '24px', 
                  background: 'rgba(255,255,255,0.15)', 
                  margin: '0 var(--space-2)' 
                }} />
                <Link to="/admin/tenants" className="nav-link nav-link-admin" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  {Icons.tenants}
                  <span>Tenants</span>
                </Link>
                <Link to="/admin/users" className="nav-link nav-link-admin" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  {Icons.users}
                  <span>Users</span>
                </Link>
              </>
            )}
            <div className="navbar-user">
              <Link to="/profile" className="nav-link-user" title="Profile">
                {Icons.profile}
                <span className="user-email">{user?.email}</span>
              </Link>
              <button onClick={logout} className="btn-logout">
                {Icons.logout}
                <span>Logout</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="navbar-links">
            <Link to="/login" className="nav-link">Sign in</Link>
            <Link 
              to="/register" 
              className="btn btn-primary" 
              style={{ 
                padding: 'var(--space-2) var(--space-4)',
                fontSize: 'var(--text-sm)'
              }}
            >
              Get started
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

// Component to show when no tenant is selected
const NoTenantSelected: React.FC = () => {
  const { isAdmin, tenants } = useAuth();

  return (
    <div className="no-tenant-container">
      <div className="no-tenant-card">
        <div style={{
          width: '72px',
          height: '72px',
          background: 'linear-gradient(135deg, var(--color-neutral-100) 0%, var(--color-neutral-200) 100%)',
          borderRadius: 'var(--radius-xl)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto var(--space-5)'
        }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--color-neutral-400)" strokeWidth="1.5">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
        </div>
        <h2>No Store Selected</h2>
        {tenants.length === 0 ? (
          <>
            <p>You don't have access to any Shopify stores yet.</p>
            {isAdmin ? (
              <Link to="/admin/tenants" className="btn btn-primary" style={{ marginTop: 'var(--space-4)' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Add a Tenant
              </Link>
            ) : (
              <p style={{ color: 'var(--color-neutral-500)', fontSize: 'var(--text-sm)' }}>
                Please contact an administrator to get access to a store.
              </p>
            )}
          </>
        ) : (
          <p>Please select a store from the dropdown above to continue.</p>
        )}
      </div>
    </div>
  );
};

/**
 * TenantRouteHandler - Handles tenant context for nested routes
 * 
 * This component:
 * 1. Extracts tenantSlug from URL params
 * 2. Validates the user has access to this tenant
 * 3. Auto-switches tenant context if needed
 * 4. Redirects to root if tenant not found or no access
 */
const TenantRouteHandler: React.FC = () => {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { 
    isAuthenticated, 
    getTenantBySlug, 
    switchTenantBySlug, 
    currentTenant,
    isLoading 
  } = useAuth();
  const navigate = useNavigate();
  const [isValidating, setIsValidating] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const validateAndSwitchTenant = async () => {
      if (!tenantSlug || isLoading) return;

      // Check if tenant exists in user's tenant list
      const tenant = getTenantBySlug(tenantSlug);
      
      if (!tenant) {
        setError(`Store "${tenantSlug}" not found or you don't have access.`);
        setIsValidating(false);
        return;
      }

      // If already on this tenant, we're good
      if (currentTenant?.shopify_shop_name === tenantSlug) {
        setIsValidating(false);
        return;
      }

      // Switch to this tenant
      const result = await switchTenantBySlug(tenantSlug);
      
      if (!result.success) {
        setError(result.error || 'Failed to access this store.');
      }
      
      setIsValidating(false);
    };

    validateAndSwitchTenant();
  }, [tenantSlug, isLoading, getTenantBySlug, switchTenantBySlug, currentTenant]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Tenant switch for this slug still pending (e.g. browser back/forward)
  const isSwitching = !error && currentTenant?.shopify_shop_name !== tenantSlug;

  if (isLoading || isValidating || isSwitching) {
    return (
      <div className="loading-screen">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'var(--space-4)'
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            background: 'linear-gradient(135deg, var(--color-primary-400) 0%, var(--color-primary-600) 100%)',
            borderRadius: 'var(--radius-xl)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'pulse 1.5s ease-in-out infinite'
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          </div>
          <span style={{ color: 'var(--color-neutral-500)', fontWeight: 500 }}>Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="no-tenant-container">
        <div className="no-tenant-card">
          <div style={{
            width: '72px',
            height: '72px',
            background: 'linear-gradient(135deg, #fed7d7 0%, #feb2b2 100%)',
            borderRadius: 'var(--radius-xl)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto var(--space-5)'
          }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#c53030" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <h2>Access Denied</h2>
          <p>{error}</p>
          <button 
            onClick={() => navigate('/')} 
            className="btn btn-primary" 
            style={{ marginTop: 'var(--space-4)' }}
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  // Render child routes. Keyed by tenant so pages remount and refetch their
  // data after a tenant switch instead of keeping the previous store's data.
  return <Outlet key={currentTenant?.id} />;
};

/**
 * RootRedirect - Handles redirect logic for the root path
 */
const RootRedirect: React.FC = () => {
  const { isAuthenticated, currentTenantSlug, hasTenant, isLoading } = useAuth();

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (hasTenant && currentTenantSlug) {
    return <Navigate to={`/t/${currentTenantSlug}/`} replace />;
  }

  // Show tenant selection if authenticated but no tenant
  return <NoTenantSelected />;
};

function AppContent() {
  const { isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="app">
        <div className="loading-screen">
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--space-4)'
          }}>
            <div style={{
              width: '56px',
              height: '56px',
              background: 'linear-gradient(135deg, var(--color-primary-400) 0%, var(--color-primary-600) 100%)',
              borderRadius: 'var(--radius-xl)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'pulse 1.5s ease-in-out infinite'
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
            </div>
            <span style={{ color: 'var(--color-neutral-500)', fontWeight: 500 }}>Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<><NavBar /><main className="main-content"><Login /></main></>} />
        <Route path="/register" element={<><NavBar /><main className="main-content"><Register /></main></>} />
        
        {/* Profile route (authenticated, no tenant required) */}
        <Route path="/profile" element={
          <ProtectedRoute>
            <NavBar />
            <main className="main-content"><Profile /></main>
          </ProtectedRoute>
        } />
        
        {/* Admin routes (no tenant required) */}
        <Route path="/admin/users" element={
          <ProtectedRoute requireAdmin>
            <NavBar />
            <main className="main-content"><AdminUsers /></main>
          </ProtectedRoute>
        } />
        <Route path="/admin/tenants" element={
          <ProtectedRoute requireAdmin>
            <NavBar />
            <main className="main-content"><AdminTenants /></main>
          </ProtectedRoute>
        } />
        
        {/* Tenant-scoped routes */}
        <Route path="/t/:tenantSlug/*" element={
          <ProtectedRoute>
            <NavBar />
            <main className="main-content">
              <TenantRouteHandler />
            </main>
          </ProtectedRoute>
        }>
          <Route index element={<Dashboard />} />
          <Route path="sync" element={<SyncShopify />} />
          <Route path="orders" element={<Orders />} />
          <Route path="orders/:id" element={<OrderDetail />} />
          <Route path="transactions" element={<Transactions />} />
          <Route path="reports/unpaid" element={<UnpaidReport />} />
          <Route path="reports/unshipped" element={<UnshippedReport />} />
          <Route path="reports/gift-cards" element={<GiftCardReport />} />
        </Route>
        
        {/* Root redirect */}
        <Route path="/" element={
          <>
            <NavBar />
            <main className="main-content">
              <RootRedirect />
            </main>
          </>
        } />
        
        {/* Catch-all - redirect to root */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
